import React, { useState } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { 
  Check, X, Zap, Crown, Rocket, ArrowRight, 
  TrendingUp, Shield, Phone, Mail, MessageSquare,
  Loader, Star, Truck
} from 'lucide-react';
import { useUser } from '../contexts/UserContext';
import { useAuth0 } from '@auth0/auth0-react';

// Initialize Stripe
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

function PricingPlans() {
  const { userRole, userData } = useUser();
  const { user } = useAuth0();
  const [loading, setLoading] = useState('');
  const [error, setError] = useState('');

  // Force new build hash - updated 2025-08-16
  const plans = [
    {
      id: 'basic',
      name: 'Basic (Free)',
      price: 0,
      priceId: null,
      icon: Star,
      color: 'gray',
      recommended: false,
      features: [
        { text: '10 searches per day', included: true },
        { text: '3 lane pricing queries per day', included: true },
        { text: '3 route optimizations per day', included: true },
        { text: 'Limited rate insights', included: true },
        { text: 'Limited broker metrics', included: true },
        { text: 'Basic heat map access', included: true },
        { text: 'Community support', included: true },
        { text: 'Priority support', included: false },
        { text: 'Advanced features', included: false },
        { text: 'API access', included: false },
        { text: 'TruckStop account required for booking', included: true }
      ],
      description: 'Get started with basic freight management tools'
    },
    {
      id: 'standard',
      name: 'Standard Plan',
      price: 29,
      priceId: process.env.REACT_APP_STRIPE_STANDARD_PRICE_ID || 'price_1RvZ8CDvj5PcWFuPjhi3c32T',
      icon: Zap,
      color: 'blue',
      recommended: false,
      features: [
        { text: '500 searches per month', included: true },
        { text: 'Unlimited lane pricing', included: true },
        { text: '100 route optimizations per month', included: true },
        { text: 'Full rate insights dashboard', included: true },
        { text: 'Full broker metrics', included: true },
        { text: 'Advanced heat map features', included: true },
        { text: 'Email support', included: true },
        { text: 'Custom integrations', included: false },
        { text: 'Dedicated account manager', included: false },
        { text: 'API access', included: false },
        { text: 'TruckStop account required for booking', included: true }
      ],
      description: 'Perfect for small to medium carriers and brokers'
    },
    {
      id: 'premium',
      name: 'Premium Plan',
      price: 59,
      priceId: process.env.REACT_APP_STRIPE_PREMIUM_PRICE_ID || 'price_1RvZ8CDvj5PcWFuPspMRJdzw',
      icon: Crown,
      color: 'purple',
      recommended: true,
      features: [
        { text: 'Unlimited searches', included: true },
        { text: 'Advanced lane pricing', included: true },
        { text: 'Unlimited route optimization', included: true },
        { text: 'Real-time rate insights', included: true },
        { text: 'Advanced broker analytics', included: true },
        { text: 'All map features', included: true },
        { text: 'Priority email support', included: true },
        { text: 'Webhook notifications', included: true },
        { text: 'Dedicated account manager', included: false },
        { text: 'API access', included: false },
        { text: 'TruckStop account required for booking', included: true }
      ],
      description: 'Ideal for growing fleets and active brokers'
    },
    {
      id: 'custom',
      name: 'Custom / API Access',
      price: null,
      priceId: null,
      icon: Rocket,
      color: 'green',
      recommended: false,
      features: [
        { text: 'Everything in Premium', included: true },
        { text: 'Unlimited API calls', included: true },
        { text: 'Custom data integrations', included: true },
        { text: 'White-label options', included: true },
        { text: 'Custom reporting', included: true },
        { text: 'SLA guarantee', included: true },
        { text: 'Phone & video support', included: true },
        { text: 'Dedicated account manager', included: true },
        { text: 'Custom feature development', included: true },
        { text: 'On-premise deployment option', included: true }
      ],
      description: 'Enterprise solutions tailored to your needs'
    }
  ];

  const handleSubscribe = async (plan) => {
    if (plan.id === 'custom') {
      // Handle custom plan contact
      window.location.href = 'mailto:support@lotwizard.us?subject=Custom Plan Inquiry';
      return;
    }

    if (plan.id === 'basic') {
      // Free/Basic plan doesn't need Stripe checkout
      // Could redirect to profile or show a message
      window.location.href = '/profile';
      return;
    }

    setLoading(plan.id);
    setError('');

    try {
      // Create checkout session via tai-khoan service (where Stripe endpoints exist)
      const isLocal = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
      const taiKhoanUrl = isLocal ? 'http://64.225.20.106:8081' : '/tai-khoan';
      
      // First, get the user's Stripe customer ID
      if (!userData?.stripe_id_live && !userData?.stripe_id_test) {
        throw new Error('Stripe customer ID not found. Please refresh and try again.');
      }
      
      // Determine if we should use test mode (for Admin/Dev users)
      const isTestMode = ['Admin', 'Dev'].includes(userRole) ? 1 : 0;
      const stripeCustomerId = isTestMode ? userData.stripe_id_test : userData.stripe_id_live;
      
      // Debug logging - v2
      console.log('Creating checkout session with:', {
        taiKhoanUrl,
        userData,
        userRole,
        isTestMode,
        stripeCustomerId,
        priceId: plan.priceId,
        userId: userData?.id
      });
      
      const response = await fetch(`${taiKhoanUrl}/create_checkout_session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        },
        body: JSON.stringify({
          user_id: userData?.id,  // The integer user ID from database
          stripe_customer_id: stripeCustomerId,
          price_id: plan.priceId,
          success_url: `${window.location.origin}/profile?subscription=success`,
          cancel_url: `${window.location.origin}/pricing?subscription=cancelled`,
          test_mode: isTestMode
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Checkout session API error:', {
          status: response.status,
          statusText: response.statusText,
          body: errorText
        });
        throw new Error(`Failed to create checkout session: ${response.status} - ${errorText}`);
      }

      const data = await response.json();
      const { checkout_url } = data;  // tai-khoan returns checkout_url, not sessionId
      
      // Show test mode notification for admin/dev users
      if (isTestMode) {
        console.log('Admin/Dev user - using Stripe test mode');
        alert('Admin/Dev Mode: Using Stripe test checkout (no real charges)');
      }
      
      // Redirect directly to Stripe Checkout URL
      window.location.href = checkout_url;
    } catch (err) {
      console.error('Subscription error:', err);
      setError(`Failed to start subscription: ${err.message}`);
      setLoading('');
    }
  };

  const getCurrentPlanBadge = (planId) => {
    const currentPlan = userRole?.toLowerCase();
    if (currentPlan === planId) {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200">
          Current Plan
        </span>
      );
    }
    return null;
  };

  const getPlanButton = (plan) => {
    const currentPlan = userRole?.toLowerCase();
    const isCurrentPlan = currentPlan === plan.id;
    const isDowngrade = (currentPlan === 'premium' && (plan.id === 'standard' || plan.id === 'basic')) ||
                        (currentPlan === 'standard' && plan.id === 'basic');
    
    if (isCurrentPlan) {
      return (
        <button 
          disabled
          className="w-full py-3 px-4 bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 rounded-lg font-semibold cursor-not-allowed"
        >
          Current Plan
        </button>
      );
    }

    if (plan.id === 'custom') {
      return (
        <button
          onClick={() => handleSubscribe(plan)}
          className="w-full py-3 px-4 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-lg font-semibold hover:from-green-600 hover:to-green-700 transition-all duration-200 flex items-center justify-center space-x-2"
        >
          <span>Contact Sales</span>
          <ArrowRight className="w-4 h-4" />
        </button>
      );
    }

    if (plan.id === 'basic') {
      return (
        <div className="w-full py-3 px-4 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 rounded-lg font-semibold text-center">
          {isCurrentPlan ? 'Current Plan' : 'Free Account'}
        </div>
      );
    }

    return (
      <button
        onClick={() => handleSubscribe(plan)}
        disabled={loading === plan.id}
        className={`w-full py-3 px-4 rounded-lg font-semibold transition-all duration-200 flex items-center justify-center space-x-2 ${
          plan.recommended
            ? 'bg-gradient-to-r from-purple-500 to-purple-600 text-white hover:from-purple-600 hover:to-purple-700'
            : 'bg-gradient-to-r from-blue-500 to-blue-600 text-white hover:from-blue-600 hover:to-blue-700'
        } disabled:opacity-50 disabled:cursor-not-allowed`}
      >
        {loading === plan.id ? (
          <>
            <Loader className="w-4 h-4 animate-spin" />
            <span>Processing...</span>
          </>
        ) : (
          <>
            <span>{isDowngrade ? 'Downgrade' : 'Upgrade'} Now</span>
            <ArrowRight className="w-4 h-4" />
          </>
        )}
      </button>
    );
  };

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      {/* Header */}
      <div className="text-center mb-12">
        <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">
          Choose Your Plan
        </h1>
        <p className="text-xl text-gray-600 dark:text-gray-400">
          Unlock powerful freight management tools and analytics
        </p>
        {userRole && (
          <div className="mt-4 inline-flex items-center space-x-2 px-4 py-2 bg-gray-100 dark:bg-gray-800 rounded-full">
            <Shield className="w-4 h-4 text-gray-600 dark:text-gray-400" />
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Current Role: <span className="text-primary-600 dark:text-primary-400">{userRole}</span>
            </span>
          </div>
        )}
      </div>

      {/* Error Message */}
      {error && (
        <div className="mb-6 p-4 bg-red-100 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
          <p className="text-red-700 dark:text-red-300">{error}</p>
        </div>
      )}

      {/* Pricing Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {plans.map((plan) => {
          const Icon = plan.icon;
          const colorClasses = {
            gray: 'from-gray-500 to-gray-600',
            blue: 'from-blue-500 to-blue-600',
            purple: 'from-purple-500 to-purple-600',
            green: 'from-green-500 to-green-600'
          };

          return (
            <div
              key={plan.id}
              className={`relative bg-white dark:bg-gray-800 rounded-2xl shadow-xl border ${
                plan.recommended 
                  ? 'border-purple-200 dark:border-purple-700' 
                  : 'border-gray-200 dark:border-gray-700'
              } overflow-hidden`}
            >
              {/* Recommended Badge */}
              {plan.recommended && (
                <div className="absolute top-0 right-0 bg-gradient-to-r from-purple-500 to-purple-600 text-white px-4 py-1 rounded-bl-lg">
                  <span className="text-xs font-bold uppercase">Most Popular</span>
                </div>
              )}

              {/* Plan Header */}
              <div className={`bg-gradient-to-r ${colorClasses[plan.color]} p-6 text-white`}>
                <div className="flex items-center justify-between mb-4">
                  <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center">
                    <Icon className="w-6 h-6" />
                  </div>
                  {getCurrentPlanBadge(plan.id)}
                </div>
                <h3 className="text-2xl font-bold mb-2">{plan.name}</h3>
                <p className="text-white/90 text-sm">{plan.description}</p>
              </div>

              {/* Pricing */}
              <div className="p-6 border-b border-gray-200 dark:border-gray-700">
                {plan.price ? (
                  <div className="flex items-baseline">
                    <span className="text-4xl font-bold text-gray-900 dark:text-white">
                      ${plan.price}
                    </span>
                    <span className="ml-2 text-gray-600 dark:text-gray-400">/month</span>
                  </div>
                ) : (
                  <div className="text-3xl font-bold text-gray-900 dark:text-white">
                    Custom Pricing
                  </div>
                )}
              </div>

              {/* Features */}
              <div className="p-6 space-y-3">
                {plan.features.map((feature, index) => (
                  <div key={index} className="flex items-start space-x-3">
                    {feature.text.includes('TruckStop account') ? (
                      <Truck className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
                    ) : feature.included ? (
                      <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                    ) : (
                      <X className="w-5 h-5 text-gray-400 flex-shrink-0 mt-0.5" />
                    )}
                    <span className={`text-sm ${
                      feature.text.includes('TruckStop account')
                        ? 'text-blue-700 dark:text-blue-300 font-medium'
                        : feature.included 
                          ? 'text-gray-700 dark:text-gray-300' 
                          : 'text-gray-400 dark:text-gray-500'
                    }`}>
                      {feature.text}
                    </span>
                  </div>
                ))}
              </div>

              {/* Action Button */}
              <div className="p-6 pt-0">
                {getPlanButton(plan)}
              </div>
            </div>
          );
        })}
      </div>

      {/* Additional Information */}
      <div className="mt-12 bg-gray-50 dark:bg-gray-800/50 rounded-xl p-8">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="text-center">
            <div className="w-12 h-12 bg-primary-100 dark:bg-primary-900 rounded-lg flex items-center justify-center mx-auto mb-3">
              <Shield className="w-6 h-6 text-primary-600 dark:text-primary-400" />
            </div>
            <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Secure Payments</h4>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              All transactions are processed securely through Stripe
            </p>
          </div>
          <div className="text-center">
            <div className="w-12 h-12 bg-primary-100 dark:bg-primary-900 rounded-lg flex items-center justify-center mx-auto mb-3">
              <TrendingUp className="w-6 h-6 text-primary-600 dark:text-primary-400" />
            </div>
            <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Cancel Anytime</h4>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              No long-term contracts, cancel or change plans anytime
            </p>
          </div>
          <div className="text-center">
            <div className="w-12 h-12 bg-primary-100 dark:bg-primary-900 rounded-lg flex items-center justify-center mx-auto mb-3">
              <MessageSquare className="w-6 h-6 text-primary-600 dark:text-primary-400" />
            </div>
            <h4 className="font-semibold text-gray-900 dark:text-white mb-2">24/7 Support</h4>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Get help when you need it from our dedicated support team
            </p>
          </div>
        </div>
      </div>

      {/* Contact Section */}
      <div className="mt-8 text-center">
        <p className="text-gray-600 dark:text-gray-400 mb-4">
          Questions about our plans or need a custom solution?
        </p>
        <div className="flex items-center justify-center space-x-4">
          <a 
            href="mailto:support@lotwizard.us"
            className="inline-flex items-center space-x-2 text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300"
          >
            <Mail className="w-4 h-4" />
            <span>support@lotwizard.us</span>
          </a>
          <span className="text-gray-400">|</span>
          <a 
            href="tel:1-800-LOTWIZARD"
            className="inline-flex items-center space-x-2 text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300"
          >
            <Phone className="w-4 h-4" />
            <span>1-800-LOTWIZARD</span>
          </a>
        </div>
      </div>
    </div>
  );
}

export default PricingPlans;